/* eslint-disable no-undef */
import { ChangeEvent, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { Form, Formik, FormikValues } from "formik";
import * as Yup from "yup";

import { ReactComponent as Image } from "../../../assets/image.svg";
import Button from "../../../custom/button/button";
import Input from "../../../custom/input/input";
import Select from "../../../custom/select/select";
import Upload from "../../../custom/upload/upload";
import { createOrUpdateHistory } from "../../../requests";
import { validator } from "../../../utils/validator";

export const CreateHistory = ({ handleClose }: { handleClose: () => void }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [upload, setUpload] = useState<File | null>(null);
  const addHistoryMutation = useMutation({ mutationFn: createOrUpdateHistory });

  const validate = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    status: validator.status,
  });

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file) {
      setUpload(file[0]);
    }
  };

  const clearFile = () => {
    setUpload(null);
  };

  const handleAddHistory = async (values: FormikValues, resetForm: () => void) => {
    const payload: Partial<SetupPayload> = {
      Title: values.title,
      Description: values.description,
      Image: upload,
      ActiveStatus: values.status === "Active",
      IsDeleted: false,
    };

    try {
      await addHistoryMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ["get-history"] });

          handleClose();
          resetForm();
          clearFile();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const statusOptions = (
    <>
      <option>Active</option>
      <option>Inactive</option>
    </>
  );

  return (
    <Formik
      initialValues={{
        title: "",
        description: "",
        status: "",
      }}
      onSubmit={(values, { resetForm }) => handleAddHistory(values, resetForm)}
      validationSchema={validate}
    >
      <Form className="fields">
        <Input name="title" label="Title" placeholder="Input title" />
        <Input
          type="textarea"
          name="description"
          label="Description"
          placeholder="Input description"
        />
        
        {upload ? (
          <div className="small-gap">
            <Image />
            <span>{upload.name}</span>
            <Button onClick={clearFile} variant="text" text="x" />
          </div>
        ) : (
          <Upload name="image" label="Image" onChange={handleFileChange} />
        )}

        <Select
          name="status"
          label="Status"
          placeholder="Select status"
          options={statusOptions}
        />

        <div className="btn-group">
          <Button
            type="button"
            onClick={handleClose}
            variant="text"
            text="Cancel"
          />
          <Button
            type="submit"
            text="Create"
            isLoading={addHistoryMutation.isPending}
            disabled={addHistoryMutation.isPending}
          />
        </div>
      </Form>
    </Formik>
  );
};

export const EditHistory = ({
  item,
  handleClose,
}: {
  item: Setup;
  handleClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { notification } = App.useApp();
  const [upload, setUpload] = useState<File | null>(null);
  const editHistoryMutation = useMutation({
    mutationFn: createOrUpdateHistory,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file) {
      setUpload(file[0]);
    }
  };

  const clearFile = () => {
    setUpload(null);
  };

  const handleEditHistory = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    let payload: Partial<SetupPayload> = {
      Id: item.id,
      Title: values.title,
      Description: values.description,
      ActiveStatus: values.status === "Active",
      IsDeleted: false,
    };

    if (upload) {
      payload.Image = upload;
    }

    try {
      await editHistoryMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          queryClient.refetchQueries({ queryKey: ["get-history"] });
          setUpload(null);
          handleClose();
          resetForm();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const statusOptions = (
    <>
      <option>Active</option>
      <option>Inactive</option>
    </>
  );

  const initialStatus =
    item?.activeStatus === true
      ? "Active"
      : item?.activeStatus === false
      ? "Inactive"
      : "";

  return (
    <Formik
      initialValues={{
        title: item?.title ?? "",
        description: item?.description ?? "",
        status: initialStatus ?? "",
      }}
      onSubmit={(values, { resetForm }) => handleEditHistory(values, resetForm)}
      enableReinitialize
    >
      <Form className="fields">
        <Input name="title" label="Title" placeholder="Input title" />
        <Input
          type="textarea"
          name="description"
          label="Description"
          placeholder="Input description"
        />

        {upload ? (
          <div className="small-gap">
            <Image />
            <span>{upload.name}</span>
            <Button onClick={clearFile} variant="text" text="x" />
          </div>
        ) : (
          <Upload name="image" label="Image" onChange={handleFileChange} />
        )}

        <Select
          name="status"
          label="Status"
          placeholder="Active"
          options={statusOptions}
        />

        <div className="btn-group">
          <Button
            type="button"
            onClick={handleClose}
            variant="text"
            text="Cancel"
          />
          <Button
            type="submit"
            text="Update"
            isLoading={editHistoryMutation.isPending}
            disabled={editHistoryMutation.isPending}
          />
        </div>
      </Form>
    </Formik>
  );
};
